// SPDX-FileCopyrightText: 2022-2026 Lablup Inc. All rights reserved.
// SPDX-License-Identifier: LicenseRef-Proprietary
import { baiFetch } from "../hooks/auth";

const DEFAULT_TIMEOUT_MS = 30_000;

/**
 * Wrapper around `baiFetch` that aborts after `timeoutMs` milliseconds.
 *
 * If the caller already provides a signal (e.g. from React Query or an
 * AbortController), this function respects it: the request aborts when
 * *either* the caller's signal or the timeout fires first.
 *
 * @param url     - The request path (same format as `baiFetch`)
 * @param init    - Standard RequestInit options
 * @param timeoutMs - Timeout in milliseconds (default: 30 000)
 */
export async function fetchWithTimeout(
  url: string,
  init?: RequestInit,
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  // If the caller provided an external signal, abort our controller when it fires.
  const externalSignal = init?.signal;
  const onExternalAbort = () => controller.abort(externalSignal?.reason);
  if (externalSignal) {
    if (externalSignal.aborted) {
      controller.abort(externalSignal.reason);
    } else {
      externalSignal.addEventListener("abort", onExternalAbort, { once: true });
    }
  }

  try {
    const response = await baiFetch(url, {
      ...init,
      signal: controller.signal,
    });
    return response;
  } finally {
    clearTimeout(timeoutId);
    if (externalSignal) {
      externalSignal.removeEventListener("abort", onExternalAbort);
    }
  }
}
